
import * as React from 'react';
import { TrackToggle, useMediaDeviceSelect } from '@livekit/components-react';
import { Track, LocalAudioTrack } from 'livekit-client';
import PreJoinVisualizer from './PreJoinVisualizer';

/** Strip hardware IDs like (3A71:F4B5) from device labels for cleaner display. */
function sanitizeDeviceLabel(label: string): string {
    return label.replace(/\s*\([0-9a-fA-F]{4}:[0-9a-fA-F]{4}\)\s*$/g, "").trim();
}

const DeviceSelector = () => {
    const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({ kind: 'audioinput' });

    return (
        <div style={{ position: 'relative', width: '36px', height: '36px' }}>
            {/* Visual Button matching Mic Toggle */}
            <div style={{
                width: '100%',
                height: '100%',
                padding: '0',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none', // Allow clicks to pass through to the select
                transition: 'all 0.2s ease',
                color: 'white'
            }}>
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </div>

            {/* Invisible Native Select Trigger */}
            <select
                value={activeDeviceId}
                onChange={(e) => setActiveMediaDevice(e.target.value)}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    appearance: 'none',
                    WebkitAppearance: 'none'
                }}
                title="Select Microphone" // Minimal accessible title
            >
                {devices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                        {sanitizeDeviceLabel(device.label || `Microphone ${device.deviceId.slice(0, 5)}...`)}
                    </option>
                ))}
            </select>
        </div>
    );
};
interface VoiceControlBarProps {
    // Mic state
    micEnabled?: boolean;
    onMicToggle?: (enabled: boolean) => void;

    // Track for visualizer
    track?: LocalAudioTrack | null;
    trackRef?: any; // For connected state

    // Action Button
    actionText: React.ReactNode;
    onActionClick: () => void;
    actionDisabled?: boolean;
    actionButtonClassName?: string;
    actionButtonStyle?: React.CSSProperties;

    // LiveKit components props
    showIcon?: boolean;
    permissionStatus?: string;
}

export const VoiceControlBar: React.FC<VoiceControlBarProps> = ({
    micEnabled,
    onMicToggle,
    track,
    trackRef,
    actionText,
    onActionClick,
    actionDisabled = false,
    actionButtonClassName,
    actionButtonStyle,
    showIcon = true,
    permissionStatus = 'prompt',
}) => {
    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px 0', // Reduced padding since no pill shape
            flexWrap: 'nowrap',
            width: 'fit-content',
            margin: '0 auto'
        }} title="">
            {/* Mic Toggle - Always use the same button component for smooth transitions */}
            <button
                onClick={() => onMicToggle && onMicToggle(!micEnabled)}
                style={{
                    padding: '0',
                    width: '36px',
                    height: '36px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    color: 'white'
                }}
                title={micEnabled ? 'Mute' : 'Unmute'}
            >
                {micEnabled ? (
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                        <line x1="12" y1="19" x2="12" y2="23" />
                        <line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                ) : (
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <line x1="1" y1="1" x2="23" y2="23" />
                        <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                        <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                        <line x1="12" y1="19" x2="12" y2="23" />
                        <line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                )}
            </button>

            {/* Visualizer */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                width: '50px',
                height: '36px',
                margin: '0 4px'
            }}>
                {/* Prioritize 'track' (LocalAudioTrack) if available, otherwise check trackRef */}
                {(track || trackRef?.publication?.track) ? (
                    <PreJoinVisualizer
                        track={(track || trackRef?.publication?.track) as LocalAudioTrack}
                        micEnabled={micEnabled}
                    />
                ) : (
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', width: '100%', height: '100%', justifyContent: 'center' }}>
                        {[...Array(5)].map((_, i) => (
                            <div key={i} style={{
                                width: '6px',
                                height: '4px',
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                borderRadius: '4px'
                            }} />
                        ))}
                    </div>
                )}
            </div>

            {/* Device Selector - Only show if permission is already granted to avoid prompt on load */}
            {permissionStatus === 'granted' && <DeviceSelector />}

            {/* Action Button */}
            <button
                className={actionButtonClassName || "lk-button"}
                onClick={onActionClick}
                disabled={actionDisabled}
                style={{
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 16px',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    borderRadius: '8px',
                    cursor: actionDisabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    minWidth: '72px',
                    boxSizing: 'border-box',
                    ...actionButtonStyle
                }}
                onMouseEnter={(e) => {
                    if (actionButtonStyle?.backgroundColor === '#ef4444') {
                        e.currentTarget.style.backgroundColor = '#dc2626';
                    }
                }}
                onMouseLeave={(e) => {
                    if (actionButtonStyle?.backgroundColor === '#ef4444') {
                        e.currentTarget.style.backgroundColor = '#ef4444';
                    }
                }}
            >
                {actionText}
            </button>
        </div>
    );
};
